<template>
  <!-- Infinite scroll detector - emits events when the user scrolls to the bottom of the page -->
  <infinite-scroll-detector
    class="duplicates-tab-not-duplicates fill-height"
    @load-more="onLoadMore"
  >
    <!-- Skeleton loader shown when the duplicate groups are loading -->
    <duplicate-group-skeleton-loader v-if="showLoadingState" />

    <!-- Duplicate groups shown when the duplicate groups are loaded -->
    <duplicate-group-card-list
      v-else
      :duplicate-groups="notDuplicateGroups"
      :total-count="notDuplicatesCount"
      empty-state-title="No duplicate resolutions found"
      empty-state-message="Duplicate resolutions are stored here and can be undone anytime."
      :hide-back-button="!showBackButton"
      @mark-as-duplicate="onMarkAsDuplicate"
      @go-back="$emit('go-back')"
    />

    <!-- Loading more indicator shown when the user scrolls to the bottom of the page -->
    <loading-more-indicator
      v-if="showLoadingMoreIndicator"
      :loading="loadingMore"
      :has-next-page="hasNextPage"
      @load-more="onLoadMore"
    />
  </infinite-scroll-detector>
</template>

<script>
import InfiniteScrollDetector from '@/components/Common/InfiniteScrollDetector';
import LoadingMoreIndicator from '@/components/Common/LoadingMoreIndicator';
import DuplicateGroupSkeletonLoader from './DuplicateGroupSkeletonLoader';
import DuplicateGroupCardList from './DuplicateGroupCardList';
import { GetReferenceDuplicateGroupsResolvedAsNotDuplicates } from '@/graphql';
import { Review, ReferenceDuplicateGroupConnection } from '@/models';
import { appendNextResults } from '@/modules/paginationHelpers';
import { apolloClient } from '@/plugins/apollo';
import { mapActions } from 'vuex';

const PAGE_SIZE = 10;

export default {
  name: 'DuplicatesTabNotDuplicates',
  components: {
    InfiniteScrollDetector,
    LoadingMoreIndicator,
    DuplicateGroupSkeletonLoader,
    DuplicateGroupCardList,
  },
  props: {
    review: {
      type: Review,
      required: true,
    },
    showBackButton: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['go-back'],
  data: () => ({
    notDuplicateGroupsConnection: null,
    loadingMore: false,
  }),
  apollo: {
    notDuplicateGroupsConnection: {
      query: GetReferenceDuplicateGroupsResolvedAsNotDuplicates,
      variables() {
        return {
          reviewId: this.review.id,
          first: PAGE_SIZE,
        };
      },
      update: (data) => {
        return new ReferenceDuplicateGroupConnection(
          data.referenceDuplicateGroupsResolvedAsNotDuplicates
        );
      },
    },
  },
  computed: {
    showLoadingState() {
      return !this.notDuplicateGroupsConnection;
    },
    showLoadingMoreIndicator() {
      return !this.showLoadingState && this.notDuplicatesCount > 0;
    },
    notDuplicateGroups() {
      return this.notDuplicateGroupsConnection?.nodes() ?? [];
    },
    notDuplicatesCount() {
      return this.review.resolvedNotDuplicatesCount;
    },
    hasNextPage() {
      return (
        this.notDuplicateGroupsConnection?.pageInfo.hasNextPage ??
        false
      );
    },
  },
  destroyed() {
    apolloClient.cache.evict({
      id: 'ROOT_QUERY',
      fieldName: 'referenceDuplicateGroupsResolvedAsNotDuplicates',
    });
  },
  methods: {
    ...mapActions({
      resolveDuplicates: 'reference/resolveDuplicates',
    }),
    async onLoadMore() {
      if (this.loadingMore || !this.hasNextPage) return;
      this.loadingMore = true;

      try {
        await this.$apollo.queries.notDuplicateGroupsConnection.fetchMore(
          {
            variables: {
              first: PAGE_SIZE,
              after: this.notDuplicateGroupsConnection.pageInfo
                .endCursor,
            },
            updateQuery: appendNextResults,
          }
        );
      } finally {
        this.loadingMore = false;
      }
    },
    onMarkAsDuplicate({ mainCopy, reference }) {
      this.resolveDuplicates({
        forReferenceId: mainCopy.id,
        removeReferenceIds: [reference.id],
      });
    },
  },
};
</script>
